import {
  Alert,
  Autocomplete,
  Button,
  CardActions,
  CardContent,
  CardHeader,
  Dialog,
  FormControl,
  FormHelperText,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import useToast from "../../../../hooks/useToast";
import useConfirm from "../../../../hooks/useConfirm";
import { TYPE_NOTIFICATION } from "../../shares/NotificationBull";
import AppButton from "../../shares/AppButton";
import StandardSkeletoon from "../../shares/loader/StandardSkeletoon";
import { PropositionTechniqueFinanciereService } from "../../../../service/PropositionTechniqueFinanciereService";
import { useQuery } from "../../../../hooks/useQuery";
import { DeleteOutline } from "@material-ui/icons";

const INIT_FORM_INFO = {
  detail: "",
  type_proposition: null,
  pj: null,
};

const INIT_FORM_ERROR = {
  detail_error: null,
  type_proposition_error: null,
  pj_error: null,
};

const TYPE_PROPOSITION = [
  { label: "Technique", value: "Technique" },
  { label: "Financiere", value: "Financiere" },
];

const PropositionTechniqueEtFinanciereForm = ({
  open,
  onFormClose,
  onFormOpen,
  idProposition,
}) => {
  const [formInfo, setFormInfo] = useState(INIT_FORM_INFO);
  const [formErrors, setFormErrors] = useState(INIT_FORM_ERROR);
  const [submited, setSubmited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const { Toast, showToast } = useToast();
  const { DialogComponent, openConfirmDialog } = useConfirm();
  const url = useQuery();

  const handleConfirmClose = () => {
    setFormInfo(INIT_FORM_INFO);
    onFormClose();
  };

  const handleFormClose = () => {
    openConfirmDialog(
      "Fermer le formulaire",
      "Voullez vous fermer le formulaire ?",
      () => {
        handleConfirmClose();
      },
      true
    );
  };

  const handleChangeformInfo = (e) => {
    const { name, value } = e.target;
    setFormInfo({ ...formInfo, [name]: value });
  };

  const handleFetchformInfo = () => {
    setFormLoading(true);
    PropositionTechniqueFinanciereService.find_by_uuid(idProposition)
      .then((res) => {
        if (res && res.data) {
          setFormInfo(res.data);
        }
      })
      .finally(() => {
        setFormLoading(false);
      });
  };

  const handleCheckDataValidation = () => {
    setFormErrors({
      detail_error: formInfo.detail ? null : "Detail est obligatoire",
      type_proposition_error: formInfo.type_proposition
        ? null
        : "Type de proposition est obligatoire",
      pj_error: formInfo.pj ? null : "Piece joint est obligatoire",
    });

    return Object.values(formErrors).every((error) => error === null);
  };

  const handleSave = () => {
    if (idProposition) {
      handleUpdate();
    } else {
      handleInsert();
    }
  };
  const handleInsert = () => {
    setSubmited(true);
    setLoading(true);
    PropositionTechniqueFinanciereService.insert(
      {
        ...formInfo,
        id_type: url.get("job_id"),
      },
      selectedFile
    )
      .then((res) => {
        if (res && res.success) {
          showToast(TYPE_NOTIFICATION.SUCCESS, res.message);
          handleConfirmClose();
        } else {
          showToast(TYPE_NOTIFICATION.ERROR, res.message);
        }
      })
      .finally(() => {
        setSubmited(false);
        setLoading(false);
      });
  };

  const handleUpdate = () => {
    setSubmited(true);
    setLoading(true);
    PropositionTechniqueFinanciereService.update(formInfo, selectedFile)
      .then((res) => {
        if (res && res.success) {
          showToast(TYPE_NOTIFICATION.SUCCESS, res.message);
          handleConfirmClose();
        } else {
          showToast(TYPE_NOTIFICATION.ERROR, res.message);
        }
      })
      .finally(() => {
        setSubmited(false);
        setLoading(false);
      });
  };

  const handleSubmit = () => {
    setSubmited(true);
    if (handleCheckDataValidation()) {
      handleSave();
    } else {
      showToast(TYPE_NOTIFICATION.ERROR, "Veuillez verifier les champs");
    }
  };

  useEffect(() => {
    if (formInfo) {
      handleCheckDataValidation();
    }
  }, [formInfo]);

  useEffect(() => {
    if (idProposition || open) {
      handleFetchformInfo();
    }
  }, [idProposition, open]);

  const handleClearFile = () => {
    setSelectedFile(null);
    const fileInput = document.getElementById("fileInput");
    if (fileInput) fileInput.value = null;
  };
  const handleDeletePj = () => {
    handleChangeformInfo({
      target: {
        name: "pj",
        value: null,
      },
    });
    handleClearFile();
  };

  const handleConfirmDeletePj = () => {
    openConfirmDialog(
      "Supprimer PJ",
      "Voullez vous supprimer cette pièce jointe?",
      () => {
        handleDeletePj();
      },
      () => {}
    );
  };

  return (
    <>
      <DialogComponent />
      <Dialog open={open} onClose={handleFormClose} fullWidth maxWidth="md">
        <Toast />
        <Grid>
          <StandardSkeletoon escalier={true} m={4} row={4} show={formLoading} />
        </Grid>
        <Paper
          sx={{ m: 3, display: formLoading ? "none" : "block" }}
          elevation={0}
        >
          <Grid>
            <CardHeader
              title={
                idProposition ? "Modifier proposition" : "Creer proposition"
              }
            />
            <CardContent>
              <Grid container>
                <FormControl fullWidth>
                  <label>Type de proposition </label>
                  <Autocomplete
                    size="small"
                    value={TYPE_PROPOSITION.find(
                      (item) => item.value === formInfo.type_proposition
                    )}
                    options={TYPE_PROPOSITION}
                    onChange={(e, v) => {
                      if (v)
                        handleChangeformInfo({
                          target: { name: "type_proposition", value: v.value },
                        });
                      else
                        handleChangeformInfo({
                          target: { name: "type_proposition", value: null },
                        });
                    }}
                    getOptionLabel={(option) => option.label ?? ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="type_proposition"
                        error={formErrors.type_proposition_error && submited}
                        value={formInfo.type_proposition || ""}
                        helperText={
                          submited ? formErrors.type_proposition_error : ""
                        }
                        label="Type de proposition"
                        placeholder="Type de proposition"
                      />
                    )}
                    sx={{ flexGrow: 1, mr: 0.5 }}
                  />
                </FormControl>
                <FormControl fullWidth>
                  <label>Detail/description</label>
                  <TextField
                    multiline
                    rows={14}
                    id="detail"
                    name="detail"
                    onChange={handleChangeformInfo}
                    size="small"
                    value={formInfo.detail || ""}
                    error={formErrors.detail_error && submited}
                    // error={formErrors.adresse_error && submited}
                    helperText={submited ? formErrors.detail_error : ""}
                    placeholder="Description"
                    fullWidth
                    aria-describedby="description_helper"
                  />
                </FormControl>
                <FormControl fullWidth>
                  <label>Piece jointe</label>
                  <Grid
                    style={{
                      display: formInfo?.pj ? "none" : "block",
                    }}
                  >
                    <input
                      type="file"
                      id="fileInput"
                      name="pj"
                      onChange={(evt) => {
                        setSelectedFile(evt.target.files[0]);
                        handleChangeformInfo({
                          target: {
                            name: "pj",
                            value: evt.target.files[0].name,
                          },
                        });
                      }}
                    />
                    <Grid mt={1}>
                      <FormHelperText
                        sx={{
                          fontSize: { xs: "0.85rem", sm: "0.95rem" },
                          display: formErrors.pj_error ? "" : "none",
                        }}
                      >
                        <Alert severity="error" variant="outlined">
                          {formErrors.pj_error}
                        </Alert>
                      </FormHelperText>
                    </Grid>
                    <Grid mt={1}>
                      <Alert severity="info" variant="outlined">
                        - Vous pouvez compresser vos fichiers en zip si
                        plusieurs. <br />
                        - Type du fichier autorisé: jpg, jpeg, png, gif, pdf,
                        zip.
                        <br />- Taille maximale: 3 Mo.
                      </Alert>
                    </Grid>
                  </Grid>
                  <Paper
                    elevation={0}
                    sx={{
                      marginTop: 1,
                      p: 0.5,
                      border: "1px solid rgba(30,30,30,0.15)",
                      display: formInfo?.pj ? "block" : "none",
                      backgroundColor: "transparent",
                    }}
                  >
                    <Grid container display={"flex"} flexDirection={"row"}>
                      <Grid
                        item
                        xs={10}
                        alignContent={"center"}
                        overflow={"hidden"}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: "0.9rem", sm: "1rem" },
                            fontFamily: "Roboto, sans-serif",
                          }}
                        >
                          {formInfo?.pj}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Grid container spacing={1} justifyContent={"flex-end"}>
                          <Grid item>
                            <Button
                              title="Supprimer la pièce jointe"
                              size="small"
                              color="error"
                              onClick={handleConfirmDeletePj}
                            >
                              <DeleteOutline />
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Paper>
                </FormControl>
              </Grid>
            </CardContent>
            <CardActions sx={{ justifyContent: "flex-end" }}>
              <AppButton
                loading={loading}
                text={"Enregistrer"}
                variant={"contained"}
                size={"medium"}
                onClick={(e) => handleSubmit()}
              />
              <AppButton
                text={"Fermer"}
                size={"medium"}
                variant={"outlined"}
                onClick={(e) => handleFormClose()}
              />
            </CardActions>
          </Grid>
        </Paper>
      </Dialog>
    </>
  );
};

export default PropositionTechniqueEtFinanciereForm;
